import React, { useState, useEffect, useCallback } from 'react';
import styles from './DraftAnalysisModal.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';

const DraftAnalysisModal = ({ isOpen, onClose, onConfirm, draftData, isLoading }) => {
    // State for the original draft
    const [originalSubject, setOriginalSubject] = useState('');
    const [originalBody, setOriginalBody] = useState('');
    const [isMounted, setIsMounted] = useState(false);

    // State for the alternative draft
    const [altSubject, setAltSubject] = useState('');
    const [altBody, setAltBody] = useState('');

    // --- All useEffects are preserved and correctly structured ---

    // Effect for handling Escape key
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    // Effect for managing mounting/unmounting and animations
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => setIsMounted(true), 10);
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                clearTimeout(timer);
                document.removeEventListener('keydown', handleKeyDown);
            };
        } else {
            setIsMounted(false);
        }
    }, [isOpen, handleKeyDown]);
    
    // Effect for setting focus on open for accessibility
    useEffect(() => {
        if (isMounted) {
            const firstFocusable = document.querySelector(`.${styles.modalContent} button, .${styles.modalContent} textarea`);
            if (firstFocusable) {
                firstFocusable.focus();
            }
        }
    }, [isMounted]);

    // Effect for populating state when new draft data is received
    useEffect(() => {
        if (draftData) {
            setOriginalSubject(draftData.subject || '');
            setOriginalBody(draftData.body || '');
            setAltSubject(draftData.alternative_subject || '');
            setAltBody(draftData.alternative_body || '');
        }
    }, [draftData]);

    // Wait for the fade-out animation to complete before removing from the DOM
    if (!isOpen && !isMounted) return null;

    // Handlers to pass the selected draft back to the parent
    const handleSelectOriginal = () => {
        onConfirm({ subject: originalSubject, body: originalBody });
    };

    const handleSelectAlternative = () => {
        onConfirm({ subject: altSubject, body: altBody });
    };

    const hasAlternative = draftData && draftData.alternative_subject;

    return (
        <div className={`${styles.modalOverlay} ${isMounted ? styles.show : ''}`} onClick={onClose}>
            <div className={`${styles.modalContent} ${isMounted ? styles.show : ''}`} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>AI-Generated Draft Analysis</h3>
                    <button onClick={onClose} className={styles.closeButton} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {isLoading ? (
                    <div className={styles.loadingContainer}>
                        <div className={styles.spinner}></div>
                        <p>Loading Analysis...</p>
                    </div>
                ) : !draftData ? (
                    <div className={styles.errorContainer}>
                        <h4>Analysis Not Available</h4>
                        <p>Could not load the generated draft. Please try again.</p>
                    </div>
                ) : (
                    // This single adaptive container is the core of the refactor.
                    // It's a valid JSX structure that will be styled by the corresponding CSS.
                    <div className={`${styles.container} ${hasAlternative ? styles.threePanelLayout : styles.twoPanelLayout}`}>
                        
                        <div className={styles.rationalePanel}>
                            <h4>AI Rationale & Risk Assessment</h4>
                            <div className={styles.readOnlyContent}>
                                <ReactMarkdown>{draftData.risk_analysis || "No rationale provided."}</ReactMarkdown>
                            </div>
                        </div>

                        <div className={styles.draftPanel}>
                            <h4>Proposed Draft (Editable)</h4>
                            <div className={styles.draftEditor}>
                                <textarea
                                    value={originalSubject}
                                    rows="1"
                                    onChange={(e) => setOriginalSubject(e.target.value)}
                                    className={styles.subjectInput}
                                    placeholder="Subject..."
                                />
                                <textarea
                                    value={originalBody}
                                    onChange={(e) => setOriginalBody(e.target.value)}
                                    className={styles.bodyTextarea}
                                    placeholder="Email body..."
                                />
                            </div>
                            {hasAlternative && (
                                <button onClick={handleSelectOriginal} className={styles.selectButton}>
                                    <FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.5rem' }} />
                                    Use This Version
                                </button>
                            )}
                        </div>

                        {hasAlternative && (
                            <div className={styles.draftPanel}>
                                <h4>Alternative Proposal</h4>
                                <div className={styles.draftEditor}>
                                    <textarea
                                        value={altSubject}
                                        rows="1"
                                        onChange={(e) => setAltSubject(e.target.value)}
                                        className={styles.subjectInput}
                                        placeholder="Alternative subject..."
                                    />
                                    <textarea
                                        value={altBody}
                                        onChange={(e) => setAltBody(e.target.value)}
                                        className={styles.bodyTextarea}
                                        placeholder="Alternative email body..."
                                    />
                                </div>
                                <button onClick={handleSelectAlternative} className={styles.selectButton}>
                                    <FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.5rem' }} />  
                                    Use This Version
                                </button>
                            </div>
                        )}

                        {!hasAlternative && (
                            <div className={styles.footerPanel}>
                                <button onClick={onClose} className={styles.footerButton}>Cancel</button>
                                <button onClick={handleSelectOriginal} className={`${styles.footerButton} ${styles.confirmButton}`}>
                                    <FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.5rem' }} />
                                    Confirm & Use Draft
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DraftAnalysisModal;